/**
 * Blog post catalogue. Add new posts by appending to the array below.
 * `body` accepts JSX-ready blocks. To author longer posts, prefer adding a
 * dedicated post component in /pages/blog/<slug>.jsx and importing it here.
 */
import React from "react";

export const POSTS = [
  {
    slug: "introducing-hedesk",
    title: "Introducing HeDesk: the clinic OS we wished existed",
    excerpt:
      "Why we built HeDesk, what makes it different, and how it cuts patient checkout from 12 minutes to 90 seconds.",
    cover: "from-blue-500/50 to-indigo-600/30",
    tag: "Announcement",
    date: "2026-06-10",
    read: "5 min",
    author: "The HeDesk Team",
    body: () => (
      <>
        <P>
          When we started talking to general physicians, pediatricians, and dermatologists across India, one
          theme kept repeating: <strong>the software they use to run their clinic is the worst part of their
          day</strong>. Excel sheets, paper registers, three different apps for SMS, payments, and reports.
        </P>
        <P>
          HeDesk is the opposite of that. One calm, professional dashboard for appointments, patients,
          prescriptions, billing, and online booking — designed for the way Indian clinics actually work.
        </P>
        <H2>The 90-second checkout</H2>
        <P>
          Watch a busy GP&rsquo;s receptionist do this in HeDesk: tap the patient&rsquo;s row → mark consultation
          done → generate bill at the clinic&rsquo;s default fee → collect payment with one-tap UPI → bill is
          paid, follow-up is scheduled, WhatsApp confirmation is queued. Under 90 seconds, every time.
        </P>
        <H2>Built for India, on purpose</H2>
        <Ul>
          <li>Razorpay payments with Cash / UPI / Card / Bank Transfer.</li>
          <li>WhatsApp share links — no SMS fees.</li>
          <li>Free microsite at <Code>clinicname.hedesk.help</Code>.</li>
          <li>DOCX and PDF exports for prescriptions and invoices.</li>
        </Ul>
        <P>
          We&rsquo;re just getting started. The patient mobile app is next. If you run a clinic and want to talk,
          write to <A>hello@hedesk.help</A>.
        </P>
      </>
    ),
  },
  {
    slug: "whatsapp-vs-sms-reminders",
    title: "Why we replaced SMS reminders with WhatsApp — and how it cut no-shows by 40%",
    excerpt:
      "SMS is expensive and unreliable. WhatsApp is free, instant, and patients actually read it. Here's how we made the switch.",
    cover: "from-emerald-500/50 to-green-600/30",
    tag: "Product",
    date: "2026-06-08",
    read: "6 min",
    author: "Dr. Anjali Mehta",
    body: () => (
      <>
        <P>
          Until last quarter, HeDesk relied on MSG91 for SMS reminders. They were reliable — but at ₹0.20 per
          message, a 1,000-patient clinic could easily burn ₹6,000 a month just on reminders. Worse, delivery
          rates on transactional SMS in India hover at 60–70% on average.
        </P>
        <H2>The WhatsApp move</H2>
        <P>
          We switched every reminder button in HeDesk to a one-tap <Code>wa.me</Code> link that opens
          WhatsApp Web (or the app on mobile) with the message and recipient prefilled. The staff member
          taps Send. Free, instant, with 99%+ read rate.
        </P>
        <H2>The numbers</H2>
        <Ul>
          <li>Average no-show rate dropped from 18% → 11% in 30 days across 12 pilot clinics.</li>
          <li>One pediatric clinic saw a 47% drop — kids&rsquo; parents read WhatsApp instantly.</li>
          <li>Cost savings: ₹5,000–₹15,000 / month per clinic.</li>
        </Ul>
        <P>
          We&rsquo;re also building WhatsApp Business API integration for clinics on the Enterprise plan so the
          reminders become fully automated. Until then, the manual one-tap flow already covers 99% of the
          value.
        </P>
      </>
    ),
  },
  {
    slug: "patient-mobile-app-api",
    title: "The HeDesk Patient App API is live",
    excerpt:
      "A secure REST API with API key + JWT auth, Razorpay-verified bookings, and a full patient self-data feed. Here's the contract.",
    cover: "from-violet-500/50 to-purple-600/30",
    tag: "Engineering",
    date: "2026-06-14",
    read: "8 min",
    author: "The HeDesk Team",
    body: () => (
      <>
        <P>
          We just shipped 14 public endpoints under <Code>/api/public/*</Code> that power the upcoming HeDesk
          patient mobile app. The contract is documented end-to-end at{" "}
          <Code>backend/PUBLIC_API_CONTRACT.md</Code> — here&rsquo;s the executive summary.
        </P>
        <H2>Security stack</H2>
        <Ul>
          <li><Strong>API key gate</Strong> — every request needs <Code>X-API-Key</Code> matched with constant-time compare.</li>
          <li><Strong>Patient JWT</Strong> — 30-day TTL, separate secret from staff JWTs.</li>
          <li><Strong>Rate limit</Strong> — sliding-window token bucket: 10/min auth, 10/min booking, 60/min default.</li>
          <li><Strong>Razorpay signature verification</Strong> — HMAC-SHA256 verified server-side before any booking is created.</li>
          <li><Strong>Audit log</Strong> — every public request lands in <Code>db.audit_logs</Code> with IP, UA, event.</li>
        </Ul>
        <H2>What the app can do</H2>
        <Ul>
          <li>Discover clinics &amp; doctors (filtered by city / specialty).</li>
          <li>Show real-time slot availability for any date.</li>
          <li>Book + pay in a single atomic flow (pay-first, idempotent on order id).</li>
          <li>Show the patient&rsquo;s own appointments, prescriptions, and invoices across every clinic they&rsquo;ve visited.</li>
        </Ul>
        <P>
          We covered the whole surface with 29 pytest cases. If you&rsquo;re building on this and have questions,
          email <A>hello@hedesk.help</A>.
        </P>
      </>
    ),
  },
];

export function getPost(slug) {
  return POSTS.find((p) => p.slug === slug);
}

/* Shared prose helpers */
export function H2({ children }) {
  return (
    <h2 className="mt-10 mb-3 text-xl font-semibold text-white tracking-tight" style={{ fontFamily: "Manrope" }}>
      {children}
    </h2>
  );
}
export function P({ children }) {
  return <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-4">{children}</p>;
}
export function Ul({ children }) {
  return <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-300 leading-relaxed mb-5 marker:text-blue-400">{children}</ul>;
}
export function Code({ children }) {
  return <code className="px-1.5 py-0.5 rounded bg-white/[0.06] text-blue-300 text-[0.85em] font-mono">{children}</code>;
}
export function Strong({ children }) {
  return <strong className="text-white font-semibold">{children}</strong>;
}
export function A({ children }) {
  const href = typeof children === "string" && children.includes("@") ? `mailto:${children}` : "#";
  return <a href={href} className="text-blue-400 hover:underline">{children}</a>;
}
